import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Plus, RotateCcw, Trash2, Loader2, Database, FileText, Image,
  Download, Clock, Play, CalendarClock, RefreshCw, ScrollText, AlertTriangle, CheckCircle2, X,
} from "lucide-react";
import {
  backupAPI,
  backupScheduleAPI,
  type BackupInfo,
  type BackupSchedule,
} from "@/lib/api";
import { toast } from "sonner";
import {
  isBackupCreateInFlight,
  onBackupCreateComplete,
  startManualBackupWithToast,
  startScheduleRunNowWithToast,
} from "./backupCreateProgress";

type HistoryFilter = "all" | "manual" | "scheduled";
type RestoreMode = "full" | "templates" | "db" | "media";

type BackupLogEntry = {
  id: string;
  title: string;
  status: string;
  timestamp?: string | null;
  mode?: string | null;
  body: string;
};

/** Normalize log API (structured entries or legacy flat `log`). */
function normalizeBackupLogEntries(res: {
  entries?: BackupLogEntry[];
  log?: string;
}): BackupLogEntry[] {
  if (Array.isArray(res.entries) && res.entries.length > 0) {
    return res.entries.map((e, i) => ({
      id: e.id || `entry-${i + 1}`,
      title: e.title || e.id || `Entrada ${i + 1}`,
      status: e.status || "unknown",
      timestamp: e.timestamp ?? null,
      mode: e.mode ?? null,
      body: e.body || "",
    }));
  }
  // Client-side parse when API only returns flat `log` (old workers / compat).
  const flat = res.log?.trim();
  if (flat) {
    const parsed = parseFlatBackupLog(flat);
    if (parsed.length > 0) return parsed;
    return [{ id: "legacy", title: "Log", status: "unknown", body: flat }];
  }
  return [];
}

/** Split flat create+restore sidecar text into accordion rows (mirrors backend markers). */
function parseFlatBackupLog(raw: string): BackupLogEntry[] {
  const text = raw.replace(/^\n+/, "");
  const headerRe = /^=== (CREATE|RESTORE)\s+(.+?)\s*===\s*$/gm;
  const matches = [...text.matchAll(headerRe)];
  if (matches.length === 0) return [];

  const entries: BackupLogEntry[] = [];
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const kind = (m[1] || "").toUpperCase();
    const meta = (m[2] || "").trim();
    const start = (m.index ?? 0) + m[0].length;
    const end = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
    const body = text.slice(start, end).replace(/^\n+|\n+$/g, "") + "\n" || "(sin salida)\n";

    if (kind === "CREATE") {
      entries.push({
        id: "create",
        title: meta ? `CREATE ${meta}` : "CREATE",
        status: "ok",
        timestamp: meta || null,
        mode: null,
        body,
      });
      continue;
    }

    const status = /\bFAIL\b/i.test(meta) ? "fail" : /\bOK\b/i.test(meta) ? "ok" : "unknown";
    const modeM = meta.match(/mode=(\S+)/i);
    const mode = modeM ? modeM[1].toLowerCase() : null;
    const statusLabel = status === "ok" ? "OK" : status === "fail" ? "FAIL" : null;
    const title = ["RESTORE", meta].filter(Boolean).join(" ");
    entries.push({
      id: `restore-${i + 1}`,
      title: statusLabel && !/\b(OK|FAIL)\b/i.test(meta) ? `${title} ${statusLabel}` : title,
      status,
      timestamp: meta || null,
      mode,
      body,
    });
  }
  // Newest restore first; keep CREATE at top if present.
  const create = entries.filter((e) => e.id === "create");
  const restores = entries.filter((e) => e.id !== "create").reverse();
  return [...create, ...restores];
}

const RESTORE_MODE_META: Record<RestoreMode, { label: string; hint: string }> = {
  full: { label: "Completo", hint: "DB + media + templates" },
  db: { label: "Solo base de datos", hint: "Solo base de datos" },
  media: { label: "Solo media", hint: "Solo archivos media" },
  templates: { label: "Solo templates", hint: "Solo templates PDF/JSON" },
};

const LAST_SCHEDULED_BANNER_DISMISS_KEY = "abs.backups.lastScheduledBannerDismissedAt";

const WEEKDAYS = [
  { value: 0, label: "Lun" },
  { value: 1, label: "Mar" },
  { value: 2, label: "Mié" },
  { value: 3, label: "Jue" },
  { value: 4, label: "Vie" },
  { value: 5, label: "Sáb" },
  { value: 6, label: "Dom" },
];

function backupType(b: BackupInfo): "manual" | "scheduled" {
  return b.type === "scheduled" ? "scheduled" : "manual";
}

export function BackupsSection() {
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingHistory, setRefreshingHistory] = useState(false);
  const [creating, setCreating] = useState(() => isBackupCreateInFlight());
  const [restoring, setRestoring] = useState<string | null>(null);
  const [bannerDismissedAt, setBannerDismissedAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_SCHEDULED_BANNER_DISMISS_KEY);
    } catch {
      return null;
    }
  });
  const [downloading, setDownloading] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
  const [confirmRestore, setConfirmRestore] = useState<{ filename: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ filename: string; name: string } | null>(null);
  const [restoreMode, setRestoreMode] = useState<RestoreMode>("full");
  const [logViewer, setLogViewer] = useState<{ filename: string; name: string } | null>(null);
  const [logEntries, setLogEntries] = useState<BackupLogEntry[]>([]);
  const [logAvailable, setLogAvailable] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logOpenId, setLogOpenId] = useState<string>("");

  const [keepLast, setKeepLast] = useState("10");
  const [keepDays, setKeepDays] = useState("30");
  const [savingRetention, setSavingRetention] = useState(false);
  const [applyingRetention, setApplyingRetention] = useState(false);

  const [schedName, setSchedName] = useState("Backup diario");
  const [schedFreq, setSchedFreq] = useState<"daily" | "weekly" | "hourly">("daily");
  const [schedHour, setSchedHour] = useState("2");
  const [schedMinute, setSchedMinute] = useState("0");
  const [schedEveryHours, setSchedEveryHours] = useState("6");
  const [schedWeekday, setSchedWeekday] = useState("0");
  const [schedLabel, setSchedLabel] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [runningScheduleId, setRunningScheduleId] = useState<number | null>(null);

  const loadBackups = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (silent) setRefreshingHistory(true);
    else setLoading(true);
    try {
      const list = await backupAPI.list();
      setBackups(list);
    } catch {
      toast.error("Error cargando backups");
    } finally {
      if (silent) setRefreshingHistory(false);
      else setLoading(false);
    }
  }, []);

  const loadSchedules = useCallback(async () => {
    try {
      const list = await backupScheduleAPI.list();
      setSchedules(list);
    } catch {
      /* schedules endpoint may be briefly unavailable during deploy */
    }
  }, []);

  const loadRetention = useCallback(async () => {
    try {
      const r = await backupAPI.getRetention();
      setKeepLast(String(r.keep_last ?? r.keepLast ?? 10));
      setKeepDays(String(r.keep_days ?? r.keepDays ?? 30));
    } catch {
      /* keep defaults */
    }
  }, []);

  useEffect(() => {
    loadBackups();
    loadSchedules();
    loadRetention();
  }, [loadBackups, loadSchedules, loadRetention]);

  useEffect(() => {
    setCreating(isBackupCreateInFlight());
    return onBackupCreateComplete(() => {
      setCreating(false);
      setRunningScheduleId(null);
      void loadBackups({ silent: true });
      void loadSchedules();
    });
  }, [loadBackups, loadSchedules]);

  const hasManual = useMemo(() => backups.some((b) => backupType(b) === "manual"), [backups]);
  const hasScheduled = useMemo(() => backups.some((b) => backupType(b) === "scheduled"), [backups]);
  const showTypeToggle = hasManual && hasScheduled;

  const recentManualBackups = useMemo(() => {
    return backups
      .filter((b) => backupType(b) === "manual")
      .sort((a, b) => {
        const ta = new Date(String(a.created_at || a.createdAt || 0)).getTime();
        const tb = new Date(String(b.created_at || b.createdAt || 0)).getTime();
        return tb - ta;
      })
      .slice(0, 8);
  }, [backups]);

  const filteredBackups = useMemo(() => {
    if (historyFilter === "all") return backups;
    return backups.filter((b) => backupType(b) === historyFilter);
  }, [backups, historyFilter]);

  /** Latest scheduled create result across jobs (success/error; ignore skipped/empty). */
  const lastScheduledRun = useMemo(() => {
    let best: { at: string; status: "success" | "error"; message: string } | null = null;
    for (const s of schedules) {
      const at = s.last_run_at || s.lastRunAt;
      const st = (s.last_status || s.lastStatus || "").toLowerCase();
      if (!at || (st !== "success" && st !== "error")) continue;
      if (!best || new Date(String(at)).getTime() > new Date(best.at).getTime()) {
        best = {
          at: String(at),
          status: st as "success" | "error",
          message: (s.last_message || s.lastMessage || "").trim(),
        };
      }
    }
    return best;
  }, [schedules]);

  const showLastScheduledBanner = useMemo(() => {
    if (!lastScheduledRun) return false;
    if (bannerDismissedAt && bannerDismissedAt === lastScheduledRun.at) return false;
    return true;
  }, [lastScheduledRun, bannerDismissedAt]);

  const dismissLastScheduledBanner = () => {
    if (!lastScheduledRun) return;
    try {
      localStorage.setItem(LAST_SCHEDULED_BANNER_DISMISS_KEY, lastScheduledRun.at);
    } catch {
      /* ignore */
    }
    setBannerDismissedAt(lastScheduledRun.at);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { started } = await startManualBackupWithToast(label || undefined);
      if (started) {
        setLabel("");
      } else {
        setCreating(false);
      }
    } catch (e: any) {
      setCreating(false);
      const msg = e?.message || "Error creando backup";
      if (e?.status === 409 || e?.code === "backup_scheduled_in_progress" || e?.code === "backup_lock_busy") {
        toast.error(msg);
      } else {
        toast.error(`Error creando backup: ${msg}`);
      }
    }
  };

  const handleRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(confirmRestore.filename);
    setConfirmRestore(null);
    try {
      const result = await backupAPI.restore(confirmRestore.filename, restoreMode);
      if (result.status === "success") {
        toast.success("Restauración completada. Recargá la página para ver los cambios.");
      } else {
        toast.error(result.message || "La restauración no se completó correctamente.");
      }
    } catch (e: any) {
      const msg = e?.message || "Error desconocido";
      toast.error(`Error restaurando: ${msg}`);
    } finally {
      setRestoring(null);
      setRestoreMode("full");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await backupAPI.remove(confirmDelete.filename);
      toast.success("Backup eliminado");
      setConfirmDelete(null);
      await loadBackups();
    } catch (e: any) {
      toast.error(e?.message || "Error eliminando");
    }
  };

  const handleDownload = async (filename: string) => {
    setDownloading(filename);
    try {
      await backupAPI.download(filename);
      toast.success("Descarga iniciada");
    } catch (e: any) {
      toast.error(e?.message || "Error descargando");
    } finally {
      setDownloading(null);
    }
  };

  const openBackupLog = async (filename: string, name: string) => {
    setLogViewer({ filename, name });
    setLogLoading(true);
    setLogEntries([]);
    setLogAvailable(false);
    setLogOpenId("");
    try {
      const res = await backupAPI.getLog(filename);
      const entries = normalizeBackupLogEntries(res);
      setLogAvailable(Boolean(res.available) || entries.length > 0);
      setLogEntries(entries);
      // Leave collapsed; click a row to expand (single accordion).
    } catch {
      setLogAvailable(false);
      setLogEntries([]);
      setLogOpenId("");
      toast.error("Error cargando log del backup");
    } finally {
      setLogLoading(false);
    }
  };

  const handleSaveRetention = async () => {
    setSavingRetention(true);
    try {
      const r = await backupAPI.setRetention({
        keep_last: Math.max(0, parseInt(keepLast, 10) || 0),
        keep_days: Math.max(0, parseInt(keepDays, 10) || 0),
      });
      setKeepLast(String(r.keep_last ?? r.keepLast ?? keepLast));
      setKeepDays(String(r.keep_days ?? r.keepDays ?? keepDays));
      toast.success("Política de retención guardada");
    } catch (e: any) {
      toast.error(e?.message || "Error guardando retención");
    } finally {
      setSavingRetention(false);
    }
  };

  const handleApplyRetention = async () => {
    setApplyingRetention(true);
    try {
      const result = await backupAPI.applyRetention();
      const n = result.deleted?.length ?? 0;
      toast.success(n > 0 ? `Retención aplicada: ${n} eliminado(s)` : "Retención aplicada: nada que eliminar");
      await loadBackups();
    } catch (e: any) {
      toast.error(e?.message || "Error aplicando retención");
    } finally {
      setApplyingRetention(false);
    }
  };

  const handleCreateSchedule = async () => {
    setSavingSchedule(true);
    try {
      const isHourly = schedFreq === "hourly";
      await backupScheduleAPI.create({
        name: schedName.trim() || "Backup programado",
        frequency: schedFreq,
        hour: isHourly ? 0 : Math.min(23, Math.max(0, parseInt(schedHour, 10) || 0)),
        minute: isHourly ? 0 : Math.min(59, Math.max(0, parseInt(schedMinute, 10) || 0)),
        every_hours: isHourly ? Math.max(1, Math.min(168, parseInt(schedEveryHours, 10) || 1)) : 1,
        weekday: schedFreq === "weekly" ? parseInt(schedWeekday, 10) : null,
        label: schedLabel.trim() || undefined,
        enabled: true,
      });
      toast.success("Job programado creado");
      setSchedLabel("");
      await loadSchedules();
    } catch (e: any) {
      toast.error(e?.message || "Error creando job");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleToggleSchedule = async (s: BackupSchedule) => {
    try {
      await backupScheduleAPI.update(s.id, { enabled: !s.enabled });
      await loadSchedules();
    } catch (e: any) {
      toast.error(e?.message || "Error actualizando job");
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    try {
      await backupScheduleAPI.remove(id);
      toast.success("Job eliminado");
      await loadSchedules();
    } catch (e: any) {
      toast.error(e?.message || "Error eliminando job");
    }
  };

  const handleRunScheduleNow = async (id: number) => {
    setRunningScheduleId(id);
    setCreating(true);
    try {
      const { started } = await startScheduleRunNowWithToast(id);
      if (!started) {
        setRunningScheduleId(null);
        setCreating(false);
      }
    } catch (e: any) {
      setRunningScheduleId(null);
      setCreating(false);
      toast.error(e?.message || "Error ejecutando job");
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatScheduleWhen = (s: BackupSchedule) => {
    if (s.frequency === "hourly") {
      const n = s.every_hours ?? s.everyHours ?? 1;
      return `Cada ${n} hora${n === 1 ? "" : "s"}`;
    }
    const hh = String(s.hour).padStart(2, "0");
    const mm = String(s.minute).padStart(2, "0");
    if (s.frequency === "weekly") {
      const day = WEEKDAYS.find((d) => d.value === (s.weekday ?? 0))?.label ?? "?";
      return `${day} a las ${hh}:${mm}`;
    }
    return `Diario a las ${hh}:${mm}`;
  };

  return (
    <div className="space-y-6">
      {showLastScheduledBanner && lastScheduledRun && (
        lastScheduledRun.status === "success" ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-200/80 bg-emerald-50/80 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-2">
                <CheckCircle2 className="h-4 w-4 shrink-0 opacity-80" />
                <span className="truncate">
                  Último programado: OK · {new Date(lastScheduledRun.at).toLocaleString("es-AR")}
                </span>
              </div>
              <p className="pl-6 text-[11px] leading-snug text-emerald-800/70 dark:text-emerald-200/70">
                Solo refleja el último job programado (no backups manuales).
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-emerald-700/70 hover:text-emerald-900 dark:text-emerald-300"
              onClick={dismissLastScheduledBanner}
              title="Ocultar hasta el próximo run"
              aria-label="Ocultar aviso"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/50 dark:text-red-100">
            <div className="flex min-w-0 items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
              <div className="min-w-0">
                <p className="font-medium">
                  Último programado falló · {new Date(lastScheduledRun.at).toLocaleString("es-AR")}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-red-800/70 dark:text-red-200/70">
                  Solo refleja el último job programado (no backups manuales).
                </p>
                {lastScheduledRun.message ? (
                  <p className="mt-0.5 truncate text-xs text-red-800/80 dark:text-red-200/80">
                    {lastScheduledRun.message}
                  </p>
                ) : null}
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-red-700/70 hover:text-red-900 dark:text-red-300"
              onClick={dismissLastScheduledBanner}
              title="Ocultar hasta el próximo run"
              aria-label="Ocultar aviso"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )
      )}

      {/* Fila superior: manual | form programado (md+ 2 cols, same height) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        <Card className="h-full">
          <CardHeader>
            <CardTitle className="text-lg">Crear backup</CardTitle>
            <CardDescription>
              Genera un respaldo completo del sistema (base de datos, templates PDF, archivos media).
              Si hay un backup programado en curso, la creación manual se rechaza (409).
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col gap-4">
            <div className="flex-1 min-h-0 flex flex-col">
              <p className="text-xs font-medium text-muted-foreground mb-2">Últimos manuales</p>
              {recentManualBackups.length === 0 ? (
                <p className="text-sm text-slate-500">Sin backups manuales aún</p>
              ) : (
                <ul className="max-h-[13rem] space-y-1.5 overflow-y-auto overscroll-contain pr-1">
                  {recentManualBackups.map((b) => {
                    const when = b.created_at || b.createdAt
                      ? new Date(String(b.created_at || b.createdAt)).toLocaleString("es-AR")
                      : "Fecha desconocida";
                    const shortName = b.label || b.name || b.filename;
                    return (
                      <li
                        key={b.filename}
                        className="flex items-baseline justify-between gap-2 text-sm leading-snug whitespace-nowrap"
                      >
                        <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">
                          {when}
                          {shortName ? (
                            <span className="text-slate-400"> · {shortName}</span>
                          ) : null}
                        </span>
                        <span className="shrink-0 text-xs text-slate-400 tabular-nums">
                          {formatSize(b.size)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <div className="flex-1">
                <Label htmlFor="backup-label" className="sr-only">Etiqueta</Label>
                <Input
                  id="backup-label"
                  placeholder="Etiqueta opcional (ej: pre-actualización)"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={creating}
                />
              </div>
              <Button onClick={handleCreate} disabled={creating} className="shrink-0">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Backup
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarClock className="h-5 w-5" />
              Nuevo job programado
            </CardTitle>
            <CardDescription>
              Diario o semanal a una hora fija, o cada N horas (intervalo).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="sched-name">Nombre</Label>
                <Input id="sched-name" value={schedName} onChange={(e) => setSchedName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sched-freq">Frecuencia</Label>
                <Select
                  value={schedFreq}
                  onValueChange={(v) => setSchedFreq(v as "daily" | "weekly" | "hourly")}
                >
                  <SelectTrigger id="sched-freq" className="w-full">
                    <SelectValue placeholder="Frecuencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diario</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="hourly">Horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {schedFreq === "weekly" && (
                <div className="space-y-1.5">
                  <Label htmlFor="sched-weekday">Día</Label>
                  <Select value={schedWeekday} onValueChange={setSchedWeekday}>
                    <SelectTrigger id="sched-weekday" className="w-full">
                      <SelectValue placeholder="Día" />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d) => (
                        <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {schedFreq === "hourly" ? (
                <div className="space-y-1.5">
                  <Label htmlFor="sched-every-hours">Cada N horas</Label>
                  <NumberField
                    id="sched-every-hours"
                    min={1}
                    max={168}
                    value={schedEveryHours}
                    onChange={(e) => setSchedEveryHours(e.target.value)}
                    title="Intervalo entre backups (no es hora del día)"
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="sched-hour">Hora del día</Label>
                    <NumberField id="sched-hour" min={0} max={23} value={schedHour} onChange={(e) => setSchedHour(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sched-minute">Minuto</Label>
                    <NumberField id="sched-minute" min={0} max={59} value={schedMinute} onChange={(e) => setSchedMinute(e.target.value)} />
                  </div>
                </>
              )}
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="sched-label">Etiqueta del backup (opcional)</Label>
                <Input id="sched-label" value={schedLabel} onChange={(e) => setSchedLabel(e.target.value)} placeholder="ej: auto-noche" />
              </div>
              <div className="sm:col-span-2">
                <Button onClick={handleCreateSchedule} disabled={savingSchedule} className="w-full">
                  {savingSchedule ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Programado
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs programados (~75%) | Política de retención (~25%) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-stretch">
        <Card className="h-full md:col-span-3 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarClock className="h-5 w-5" />
              Jobs programados
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1">
            {schedules.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">No hay jobs programados.</p>
            ) : (
              <div className="max-h-[calc(3*2.75rem+2*0.375rem)] space-y-1.5 overflow-y-auto overscroll-contain pr-1">
                {schedules.map((s) => {
                  const lastAt = s.last_run_at || s.lastRunAt;
                  const lastLine = lastAt
                    ? `Último ${new Date(String(lastAt)).toLocaleString("es-AR")}`
                    : "Sin ejecuciones";
                  return (
                    <div
                      key={s.id}
                      className="border rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-1.5 text-sm whitespace-nowrap overflow-hidden">
                        <Clock className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span className="truncate text-slate-700 dark:text-slate-200" title={s.name}>
                          {formatScheduleWhen(s)}
                          <span className="text-slate-400"> | {lastLine}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Switch checked={s.enabled} onCheckedChange={() => handleToggleSchedule(s)} />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={runningScheduleId === s.id}
                          onClick={() => handleRunScheduleNow(s.id)}
                          title="Ejecutar ahora"
                        >
                          {runningScheduleId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={deleteIconButtonClass}
                          onClick={() => handleDeleteSchedule(s.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="h-full md:col-span-1 flex flex-col gap-4 pb-4">
          <CardHeader>
            <CardTitle className="text-lg">Política de retención</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 space-y-3">
            <div className="flex flex-col gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="keep-last">Últimos N programados</Label>
                <NumberField
                  id="keep-last"
                  min={0}
                  className="w-full"
                  value={keepLast}
                  onChange={(e) => setKeepLast(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="keep-days">Conservar N días</Label>
                <NumberField
                  id="keep-days"
                  min={0}
                  className="w-full"
                  value={keepDays}
                  onChange={(e) => setKeepDays(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="mt-auto justify-end gap-2 border-t pt-3 [.border-t]:pt-3">
            <Button onClick={handleSaveRetention} disabled={savingRetention} variant="outline">
              {savingRetention ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
            <Button onClick={handleApplyRetention} disabled={applyingRetention}>
              {applyingRetention ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Aplicar ahora
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Backup list */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <div className="min-w-0">
                <CardTitle className="text-lg">Historial de backups</CardTitle>
                <CardDescription>
                  {filteredBackups.length} backup{filteredBackups.length !== 1 ? "s" : ""}
                  {historyFilter !== "all" ? ` (${historyFilter})` : ""}
                </CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                title="Actualizar historial"
                aria-label="Actualizar historial"
                disabled={loading || refreshingHistory}
                onClick={() => void loadBackups({ silent: true })}
              >
                <RefreshCw className={`h-4 w-4 ${refreshingHistory ? "animate-spin opacity-50" : ""}`} />
              </Button>
            </div>
            {(showTypeToggle || hasScheduled || hasManual) && (
              <Tabs
                value={showTypeToggle ? historyFilter : "all"}
                onValueChange={(v) => setHistoryFilter(v as HistoryFilter)}
              >
                <TabsList>
                  <TabsTrigger value="all">Todos</TabsTrigger>
                  <TabsTrigger value="manual" disabled={!hasManual && historyFilter !== "manual"}>
                    Manuales
                  </TabsTrigger>
                  <TabsTrigger value="scheduled" disabled={!hasScheduled && historyFilter !== "scheduled"}>
                    Programados
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : filteredBackups.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No hay backups{historyFilter !== "all" ? ` ${historyFilter === "manual" ? "manuales" : "programados"}` : " creados"}.</p>
          ) : (
            <div className="max-h-[calc(10*6.5rem+9*0.75rem)] space-y-3 overflow-y-auto overscroll-contain pr-1">
              {filteredBackups.map((b) => {
                const t = backupType(b);
                return (
                  <div key={b.filename} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{b.name || b.filename}</p>
                          <span
                            className={
                              t === "scheduled"
                                ? "text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full dark:bg-violet-900 dark:text-violet-300"
                                : "text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full dark:bg-emerald-900 dark:text-emerald-300"
                            }
                          >
                            {t === "scheduled" ? "Programado" : "Manual"}
                          </span>
                          {b.label && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">{b.label}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {b.created_at || b.createdAt
                            ? new Date(String(b.created_at || b.createdAt)).toLocaleString("es-AR")
                            : "Fecha desconocida"}
                          {" · "}{formatSize(b.size)}
                        </p>
                        <div className="flex gap-3 mt-2 text-xs text-slate-400">
                          {b.components.includes("database") && <span className="flex items-center gap-1"><Database className="h-3 w-3" /> DB</span>}
                          {(b.pdf_template_count ?? b.pdfTemplateCount ?? 0) > 0 && (
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" /> {b.pdf_template_count ?? b.pdfTemplateCount} templates
                            </span>
                          )}
                          {b.components.includes("media") && <span className="flex items-center gap-1"><Image className="h-3 w-3" /> Media</span>}
                          {(b.git_commit || b.gitCommit) && (
                            <span className="font-mono">{String(b.git_commit || b.gitCommit).substring(0, 7)}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={downloading === b.filename}
                          onClick={() => handleDownload(b.filename)}
                          title="Descargar"
                        >
                          {downloading === b.filename ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={restoring === b.filename}
                          onClick={() => { setRestoreMode("full"); setConfirmRestore({ filename: b.filename, name: b.name || b.filename }); }}
                          title="Restaurar"
                        >
                          {restoring === b.filename ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={logLoading && logViewer?.filename === b.filename}
                          onClick={() => void openBackupLog(b.filename, b.name || b.filename)}
                          title="Ver log"
                          aria-label="Ver log"
                        >
                          {logLoading && logViewer?.filename === b.filename
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <ScrollText className="h-4 w-4" />}
                        </Button>
                        {t === "manual" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className={deleteIconButtonClass}
                            onClick={() => setConfirmDelete({ filename: b.filename, name: b.name || b.filename })}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            className="text-slate-300"
                            title="Los programados solo se eliminan por retención"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore confirmation */}
      <AlertDialog open={!!confirmRestore} onOpenChange={() => setConfirmRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar backup</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Restaurar desde <strong>{confirmRestore?.name}</strong>? Esta acción puede sobrescribir datos actuales.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2 py-2">
            {(["full", "db", "media", "templates"] as const).map((m) => (
              <Button
                key={m}
                variant={restoreMode === m ? "default" : "outline"}
                size="sm"
                className="h-auto flex-col items-start gap-0.5 py-2 text-left"
                onClick={() => setRestoreMode(m)}
                title={RESTORE_MODE_META[m].hint}
              >
                <span>{RESTORE_MODE_META[m].label}</span>
                <span
                  className={
                    restoreMode === m
                      ? "text-[10px] font-normal opacity-90"
                      : "text-[10px] font-normal text-muted-foreground"
                  }
                >
                  {RESTORE_MODE_META[m].hint}
                </span>
              </Button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              className="bg-[#1976D2] text-white hover:bg-[#1565C0] focus-visible:ring-[#1976D2]/40 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 dark:focus-visible:ring-blue-400/40"
            >
              Confirmar restauración
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar backup</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar <strong>{confirmDelete?.name}</strong>? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Backup console log — historial create + restores */}
      <Dialog
        open={!!logViewer}
        onOpenChange={(open) => {
          if (!open) {
            setLogViewer(null);
            setLogEntries([]);
            setLogAvailable(false);
            setLogOpenId("");
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5" />
              Log del backup
            </DialogTitle>
            <DialogDescription className="truncate font-mono text-xs">
              {logViewer?.name}
            </DialogDescription>
          </DialogHeader>
          {logLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : !logAvailable || logEntries.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Sin log disponible para este backup
            </p>
          ) : (
            <Accordion
              type="single"
              collapsible
              value={logOpenId}
              onValueChange={setLogOpenId}
              className="max-h-[min(60vh,28rem)] overflow-y-auto rounded-md border bg-muted/20"
            >
              {logEntries.map((entry) => {
                const isCreate = entry.id === "create";
                const statusTone =
                  entry.status === "fail"
                    ? "text-red-600 dark:text-red-400"
                    : entry.status === "ok"
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-foreground";
                return (
                  <AccordionItem
                    key={entry.id}
                    value={entry.id}
                    className={
                      isCreate
                        ? "border-b border-dashed bg-muted/40 px-3"
                        : "px-3"
                    }
                  >
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <span
                        className={`min-w-0 flex-1 truncate text-left font-mono text-xs font-medium tracking-tight sm:text-sm ${statusTone}`}
                      >
                        {entry.title}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <pre className="max-h-56 overflow-auto rounded-md border border-slate-700 bg-slate-950 p-3 font-mono text-xs leading-relaxed text-slate-100 whitespace-pre-wrap break-words">
                        {entry.body}
                      </pre>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
